import { ShieldCheck, Award, Briefcase } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader, CtaBand, SectionHeading } from "@/components/sections";
import { site } from "@/config/site";

export default function About() {
  const cred = site.credentials;
  return (
    <>
      <Seo
        title="About — Evan Dreyer, Professional Accountant (SA)"
        description="Bonsai Financial is led by Evan Dreyer, a SAIPA-registered Professional Accountant (SA) and Tax Technician Practitioner, providing practical accounting and tax support in South Africa."
        path="/about"
      />
      <PageHeader
        eyebrow="About Bonsai"
        title="Professional accounting with a practical, human approach."
        intro="Bonsai Financial provides practical accounting, bookkeeping, taxation and financial support to South African individuals, professionals and businesses."
      />

      <section className="py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.3fr] lg:px-8">
          {/* Founder card — headshot placeholder (no stock photo used as Evan) */}
          <div>
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div
                data-testid="founder-photo-placeholder"
                className="flex aspect-[4/5] w-full items-center justify-center bg-gradient-to-br from-[#133827] to-[#2C5E43]"
              >
                <div className="text-center text-white/90">
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/10 text-3xl font-display font-bold">
                    ED
                  </div>
                  <p className="mt-4 text-sm text-white/70">Professional headshot to be supplied</p>
                </div>
              </div>
              <div className="p-6">
                <h2 className="font-display text-xl font-bold text-[#18201B]">{site.founderName}</h2>
                <p className="mt-1 text-sm text-[#526057]">Founder</p>
                <ul className="mt-4 space-y-2 text-sm text-[#526057]">
                  <li className="flex items-center gap-2"><Award className="h-4 w-4 text-[#C68B59]" /> {cred.designation}</li>
                  <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#C68B59]" /> SAIPA Member{cred.showMembershipNumbers && cred.saipaMembershipNumber ? ` (#${cred.saipaMembershipNumber})` : ""}</li>
                  <li className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#C68B59]" /> {cred.taxTechnician}</li>
                  {cred.sait && <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#C68B59]" /> SAIT Member{cred.showMembershipNumbers && cred.saitMembershipNumber ? ` (#${cred.saitMembershipNumber})` : ""}</li>}
                </ul>
                {cred.showMembershipNumbers && (cred.practiceNumber) && (
                  <p className="mt-3 text-xs text-[#526057]">Practice #: {cred.practiceNumber}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <SectionHeading eyebrow="Our Approach" title="Combining professional expertise with accessibility." />
            <div className="mt-6 space-y-4 text-base leading-relaxed text-[#526057]">
              <p>
                Bonsai Financial provides professional accounting, bookkeeping, taxation and
                financial support to South African individuals, entrepreneurs, professionals and
                growing businesses.
              </p>
              <p>
                Led by {site.founderName}, a {cred.designation} and SAIPA member, Bonsai Financial
                combines professional accounting expertise with a practical, accessible approach to
                financial administration and tax support.
              </p>
              <p>
                Bonsai Financial has historically grown through referrals and word of mouth, and is
                now expanding to support more South African professionals and businesses.
              </p>
            </div>

            {/* Editable placeholder areas for later-supplied, verified information */}
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[
                ["Professional biography", "A fuller biography will be added here once supplied."],
                ["Experience", "Verified experience details to be confirmed."],
                ["Qualifications", "Academic qualifications to be added once confirmed."],
                ["Professional registrations", "Additional verified registrations to be listed here."],
              ].map(([t, d]) => (
                <div key={t} className="rounded-xl border border-dashed border-slate-300 bg-white/60 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#2C5E43]">{t}</p>
                  <p className="mt-1 text-sm text-[#526057]">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand from="about-cta" />
    </>
  );
}
